import {
  BaseRepository,
  IEnumerableResponse,
  ITableRequestParams
} from '@app/src/common/repository/BaseRepository';
import axios, { AxiosPromise } from 'axios';
import {
  IUser,
  IDeviceGroupManager,
  IRole,
  IRoleListItem,
  IRoleNamedItem,
  UserRoleType,
  IApiKey,
  IUserForUpdate,
  IUserInList,
  IRoleForCreate,
  IRoleForUpdating,
  IUserDocument
} from '@app/src/components/user-management/model/User';
import { IAuditLogs } from '@app/src/components/user-management/pages/audit-logs/AuditLogsTableState';
import {
  ICompany,
  ICompanyFeature,
  ICompanyInList,
  ICompanyLicense,
  ICompanyLicenseForActivation,
  ICompanyLicenseItem,
  IPlugin,
  IRestrictedCompany,
  LicenseFormObject
} from '@app/src/components/user-management/model/Company';
import { INamedItem } from '@app/src/common/model/ListModel';
import { IAuthUser, IToken } from '@app/src/common/model/AuthModel';
import { IModule } from '@app/src/components/user-management/model/Module';
import { ISession } from '@app/src/common/model/Session';

export interface IUserParams {
  startIndex?: number;
  endIndex?: number;
  search?: string;
  sort?: string;
}

export interface ICompanyModulesParams {
  companyId?: string;
  moduleKey?: string;
  startIndex?: number;
  endIndex?: number;
}

export interface ICompanyStorage {
  usedStorageSize: number;
  storageSize: number;
}

export interface ICompanyRequestParams extends ITableRequestParams {
  parentId?: string;
}

export interface IGetRoleNamesParams {
  permissions?: string;
  companyId?: string;
  companyUserId?: string;
  types?: UserRoleType[];
}

export type TokenString = string | null;

export class UserManagementRepository extends BaseRepository {
  companyUrl = `${this.userManagementUrl}/companies`;

  public signIn(params: { email?: string; password?: string }) {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    return axios.post<{ access_token: string; refresh_token: string }>(
      `${this.userManagementUrl}/auth/signin`,
      {
        email: params.email,
        password: params.password
      },
      config
    );
  }

  public refresh(params: { refreshToken: TokenString }, signal?: AbortSignal) {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      },
      signal
    };
    return axios.post<IToken>(
      `${this.userManagementUrl}/auth/refresh`,
      {
        refreshToken: params.refreshToken
      },
      config
    );
  }

  public signInWithCode(code: string) {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    return axios.post<IToken>(
      `${this.userManagementUrl}/auth/token-from-code`,
      { code },
      config
    );
  }

  public supportSignIn(companyId: string) {
    return this.callPost<IToken>(
      `${this.userManagementUrl}/auth/signin-support`,
      { companyId }
    );
  }

  public signOut(params: { refreshToken: TokenString; idToken?: TokenString }) {
    return this.callPost<{ logoutUrl?: string }>(
      `${this.userManagementUrl}/auth/signout`,
      params
    );
  }

  public getAuthedUserInfo(): Promise<IAuthUser> {
    return this.callGet<IAuthUser>(
      `${this.userManagementUrl}/users/user-info`
    ).then(response => response.data);
  }

  public getUsers(
    params?: IUserParams
  ): AxiosPromise<IEnumerableResponse<IUserInList>> {
    return this.callGet<IEnumerableResponse<IUserInList>, IUserParams>(
      `${this.userManagementUrl}/users`,
      params
    );
  }

  public getUserNames(params?: IUserParams): Promise<[IDeviceGroupManager]> {
    return this.callGet<[IDeviceGroupManager], IUserParams>(
      `${this.userManagementUrl}/users/names`,
      params
    ).then(response => response.data);
  }

  public getUser(userId: string, params?: ITableRequestParams): Promise<IUser> {
    return this.callGet<IUser, ITableRequestParams>(
      `${this.userManagementUrl}/users/${userId}`,
      params
    ).then(response => response.data);
  }

  public createUser(user: IUserForUpdate): Promise<string> {
    return this.callPost<string>(`${this.userManagementUrl}/users`, user).then(
      res => res.data
    );
  }

  public updateUser(user: IUserForUpdate): AxiosPromise<IUser> {
    return this.callPut<IUser>(
      `${this.userManagementUrl}/users/${user.id}`,
      user
    );
  }

  public sendResetPasswordLetter(email: string): AxiosPromise<string> {
    return axios.post<string>(
      `${this.userManagementUrl}/users/request-reset-password`,
      {
        email
      }
    );
  }

  public resetUserPassword(params: {
    userId: string;
    accessKey: string;
    password: string;
  }): AxiosPromise<IUser> {
    return axios.post<IUser>(`${this.userManagementUrl}/users/reset-password`, {
      userId: params.userId,
      accessKey: params.accessKey,
      password: params.password
    });
  }

  public deleteUser(userId: string): AxiosPromise<void> {
    return this.callDelete(`${this.userManagementUrl}/users/${userId}`);
  }

  public changeUserPassword(params: {
    userId: string;
    oldPassword: string;
    password: string;
  }): AxiosPromise<IUser> {
    return this.callPost(
      `${this.userManagementUrl}/users/change-password`,
      params
    );
  }

  public saveUserPreferences(
    userId: string,
    preferences: string | null
  ): AxiosPromise<void> {
    return this.callPost(
      `${this.userManagementUrl}/users/${userId}/preferences`,
      { preferences }
    );
  }

  public getAPIKey(userId: string): Promise<IApiKey> {
    return this.callGet<IApiKey>(
      `${this.userManagementUrl}/users/${userId}/apiKey`
    ).then(response => response.data);
  }

  public generateAPIKey(userId: string): Promise<IApiKey> {
    return this.callPost<IApiKey>(
      `${this.userManagementUrl}/users/${userId}/apiKey/generate`
    ).then(response => response.data);
  }

  public deleteAPIKey(userId: string) {
    return this.callDelete(`${this.userManagementUrl}/users/${userId}/apiKey`);
  }

  public unlockUser(userId: string) {
    return this.callPost(`${this.userManagementUrl}/users/${userId}/unlock`);
  }

  public inviteUser(userId: string) {
    return this.callPost(`${this.userManagementUrl}/users/${userId}/invite`);
  }

  public getUserDocuments(
    userId: string,
    params: ITableRequestParams
  ): Promise<IEnumerableResponse<IUserDocument>> {
    return this.callGet<IEnumerableResponse<IUserDocument>>(
      `${this.userManagementUrl}/users/${userId}/consents`,
      params
    ).then(response => response.data);
  }

  public acceptConsent(consentFileId: string) {
    return this.callPost(`${this.userManagementUrl}/users/consent-sign`, {
      consentFileId
    });
  }

  public getCompanyModules(
    params?: ICompanyModulesParams
  ): AxiosPromise<{ companyModules: any[] }> {
    return this.callGet(`${this.userManagementUrl}/CompanyModules`, params);
  }

  public getAuditLogs(params: {}): AxiosPromise<{
    auditLogs: IAuditLogs[];
    total: number;
  }> {
    return this.callGet(`${this.userManagementUrl}/AuditLogs`, params);
  }

  public getActivePlugins(params?: { companyId: string }): Promise<string[]> {
    return this.callGet<{ name: string }[]>(
      `${this.userManagementUrl}/plugins/activated`,
      params
    ).then(response => response.data.map(item => item.name));
  }

  /* Roles API */

  public getRoles(
    params?: ITableRequestParams
  ): Promise<IEnumerableResponse<IRoleListItem>> {
    const newParams = {
      ...(params || {}),
      types: [UserRoleType.Default, UserRoleType.Custom]
    };
    return this.callGet<IEnumerableResponse<IRoleListItem>>(
      `${this.userManagementUrl}/roles`,
      newParams
    ).then(response => response.data);
  }

  public getRolesInRole(roleId: string) {
    return this.callGet<IEnumerableResponse<IRole>>(
      `${this.userManagementUrl}/roles/${roleId}/roles`,
      { includeDeleted: true }
    ).then(res => res.data);
  }

  public getRole(roleId: string) {
    return this.callGet<IRole>(
      `${this.userManagementUrl}/roles/${roleId}`
    ).then(res => res.data);
  }

  public deleteRole(id: string): AxiosPromise<void> {
    return this.callDelete(`${this.userManagementUrl}/roles/${id}`);
  }

  public createRole(role: IRoleForCreate): AxiosPromise<string> {
    return this.callPost<string>(`${this.userManagementUrl}/roles`, role);
  }

  public updateRole(role: IRoleForUpdating): AxiosPromise<string> {
    return this.callPut<string>(`${this.userManagementUrl}/roles`, role);
  }

  public deleteRolePermissions(
    id: string,
    permissions: string[]
  ): AxiosPromise<void> {
    return this.callDelete(
      `${this.userManagementUrl}/roles/${id}/permissions`,
      permissions
    );
  }

  public updateRolePermissions(
    id: string,
    permissions: string[]
  ): AxiosPromise<void> {
    return this.callPost(
      `${this.userManagementUrl}/roles/${id}/permissions`,
      permissions
    );
  }

  public getRoleNames(params?: IGetRoleNamesParams) {
    return this.callGet<IRoleNamedItem[]>(
      `${this.userManagementUrl}/roles/names`,
      params
    ).then(res => res.data);
  }

  /* Company API */
  public getCompanies(
    params?: ICompanyRequestParams
  ): AxiosPromise<IEnumerableResponse<ICompanyInList>> {
    return this.callGet(this.companyUrl, params);
  }

  public getCompanyNames(monitoring?: boolean): AxiosPromise<INamedItem[]> {
    return this.callGet(`${this.companyUrl}/names`, { monitoring });
  }

  public getCompany(companyId: string): Promise<ICompany> {
    return this.callGet<ICompany>(`${this.companyUrl}/${companyId}`).then(
      response => response.data
    );
  }

  public createCompany(company: ICompany): Promise<string> {
    return this.callPost<string>(this.companyUrl, { ...company }).then(
      response => response.data
    );
  }

  public updateCompany(company: ICompany): Promise<string> {
    return this.callPut<string>(`${this.companyUrl}/${company.id}`, {
      ...company
    }).then(response => response.data);
  }

  public deleteCompany(id: string): AxiosPromise<void> {
    return this.callDelete(`${this.companyUrl}/${id}`);
  }

  public getTenantRoles(tenantId: string): Promise<IRole[]> {
    return this.callGet<IRole[]>(
      `${this.companyUrl}/${tenantId}/assigned-roles`
    ).then(response => response.data);
  }

  public updateTenantRoles(tenantId: string, roleIds: string[]) {
    return this.callPut<void>(`${this.companyUrl}/${tenantId}/assigned-roles`, {
      roleIds
    });
  }

  public updateRestrictedCompany(company: IRestrictedCompany, id: string) {
    return this.callPut(`${this.companyUrl}/${id}/by-company-admin`, company);
  }

  public startCompanyMonitoring(companyId: string): AxiosPromise {
    return this.callPut(`${this.companyUrl}/${companyId}/start-monitoring`, {});
  }

  public stopCompanyMonitoring(companyId: string): AxiosPromise {
    return this.callPut(`${this.companyUrl}/${companyId}/stop-monitoring`, {});
  }

  public downloadCompanies(params?: ITableRequestParams) {
    const url = `${this.companyUrl}/features/download`;
    return this.callGet<Blob>(url, params, {
      responseType: 'blob'
    }).then(result => result.data);
  }

  public getCompanyUsers(
    companyId: string,
    params?: IUserParams
  ): AxiosPromise<IEnumerableResponse<IUserInList>> {
    return this.callGet<IEnumerableResponse<IUserInList>, IUserParams>(
      `${this.companyUrl}/${companyId}/users`,
      params
    );
  }

  public getCompanyLicenses(
    companyId?: string,
    params?: ITableRequestParams
  ): AxiosPromise<IEnumerableResponse<ICompanyLicenseItem>> {
    const url = companyId
      ? `${this.companyUrl}/${companyId}/licenses`
      : `${this.companyUrl}/licenses`;
    return this.callGet(url, params);
  }

  public downloadLicenses(params?: ITableRequestParams) {
    const url = `${this.companyUrl}/licenses/download`;
    return this.callGet<Blob>(url, params, {
      responseType: 'blob'
    }).then(result => result.data);
  }

  public getLicense(licenseId: string): Promise<ICompanyLicense> {
    return this.callGet<ICompanyLicense>(
      `${this.userManagementUrl}/licenses/${licenseId}`
    ).then(response => response.data);
  }

  public getPluginLicense(licenseId: string): Promise<ICompanyLicense> {
    return this.callGet<ICompanyLicense>(
      `${this.userManagementUrl}/pluginLicenses/${licenseId}`
    ).then(response => response.data);
  }

  public getPlugins(): AxiosPromise<IPlugin[]> {
    return this.callGet(`${this.userManagementUrl}/plugins`);
  }

  public createPluginLicense(
    pluginLicense: LicenseFormObject
  ): AxiosPromise<string> {
    return this.callPost(
      `${this.userManagementUrl}/pluginLicenses`,
      pluginLicense
    );
  }

  public createCoreLicense(
    coreLicense: LicenseFormObject
  ): AxiosPromise<string> {
    return this.callPost(`${this.userManagementUrl}/Licenses`, coreLicense);
  }

  public editPluginLicense(
    pluginLicense: LicenseFormObject,
    licenseId: string
  ): AxiosPromise<string> {
    return this.callPut(
      `${this.userManagementUrl}/pluginLicenses/${licenseId}`,
      pluginLicense
    );
  }

  public editCoreLicense(
    pluginLicense: LicenseFormObject,
    licenseId: string
  ): AxiosPromise<string> {
    return this.callPut(
      `${this.userManagementUrl}/licenses/${licenseId}`,
      pluginLicense
    );
  }

  public deleteCoreLicense(licenseId: string): AxiosPromise<void> {
    return this.callDelete(`${this.userManagementUrl}/licenses/${licenseId}`);
  }

  public deletePluginLicense(licenseId: string): AxiosPromise<void> {
    return this.callDelete(
      `${this.userManagementUrl}/pluginLicenses/${licenseId}`
    );
  }

  public uploadLicense(
    formData: FormData,
    companyId: string
  ): AxiosPromise<void> {
    return this.callPost(
      `${this.companyUrl}/${companyId}/upload-licenses`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  }

  public getLicenseForActivation(
    companyId: string,
    currentDevice: string
  ): Promise<ICompanyLicenseForActivation[]> {
    return this.callGet<ICompanyLicenseForActivation[]>(
      `${this.companyUrl}/${companyId}/licenses-for-device-activation`,
      { currentDevice }
    ).then(response => response.data);
  }

  public getCompanyStorage(companyId: string): Promise<ICompanyStorage> {
    return this.callGet<ICompanyStorage>(
      `${this.companyUrl}/${companyId}/storage`
    ).then(response => response.data);
  }

  public createDemoContentForUser(userId: string): AxiosPromise {
    return this.callPost(
      `${this.projectManagementUrl}/demoContent/copyToUser`,
      { userId }
    );
  }

  public getModules(): Promise<{ modules: IModule[]; total: number }> {
    return this.callGet<{ modules: IModule[]; total: number }>(
      `${this.userManagementUrl}/modules`
    ).then(result => result.data);
  }

  public getHelpUrl(
    companyId: string,
    params: { key: string; language?: string }
  ): Promise<{ value: string }> {
    return this.callGet<{ value: string }>(
      `${this.companyUrl}/${companyId}/help-link`,
      params
    ).then(result => result.data);
  }

  public getCompanyFeatures(): Promise<IEnumerableResponse<ICompanyFeature>> {
    return this.callGet<IEnumerableResponse<ICompanyFeature>>(
      `${this.userManagementUrl}/features`
    ).then(result => result.data);
  }

  public getMySessions(): Promise<ISession[]> {
    return this.callGet<IEnumerableResponse<ISession>>(
      `${this.userManagementUrl}/users/me/sessions`
    ).then(response => response.data.items);
  }

  public terminateMyOtherSessions(): AxiosPromise {
    return this.callDeleteWithParams(
      `${this.userManagementUrl}/users/me/sessions`,
      { exceptCurrent: true }
    );
  }

  public terminateMySession(sessionId: string): AxiosPromise {
    return this.callDelete(
      `${this.userManagementUrl}/users/me/sessions/${sessionId}`
    );
  }

  public getUserSessions(userId: string): Promise<ISession[]> {
    return this.callGet<IEnumerableResponse<ISession>>(
      `${this.userManagementUrl}/users/${userId}/sessions`
    ).then(response => response.data.items);
  }

  public terminateUserSessions(userId: string): AxiosPromise {
    return this.callDelete(
      `${this.userManagementUrl}/users/${userId}/sessions`
    );
  }

  public terminateUserSession(userId: string, sessionId: string): AxiosPromise {
    return this.callDelete(
      `${this.userManagementUrl}/users/${userId}/sessions/${sessionId}`
    );
  }
}

export default new UserManagementRepository();
